import React, { useEffect, useState } from "react";
import { libraryIndex, libraryDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Beaker, Milestone, ShieldAlert, BookOpen, Clock, FileDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ngp_jwt";

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-ink/15 pb-3 mb-5">
        {Icon && <Icon size={14} className="text-oxred" />}
        <h3 className="font-mono-tight text-[11px] text-ink/80">§ {title}</h3>
      </div>
      {children}
    </section>
  );
}

function KV({ items }) {
  return (
    <dl className="divide-y divide-ink/10 border-y border-ink/15">
      {items.map(([k, v]) => (
        <div key={k} className="grid grid-cols-12 gap-4 py-3">
          <dt className="col-span-4 font-mono-tight text-[10px] text-ink/60 pt-1 uppercase">{k}</dt>
          <dd className="col-span-8 text-ink/90 text-sm leading-relaxed whitespace-pre-wrap">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const stageColor = (stage = "") => {
  const s = stage.toLowerCase();
  if (s.includes("phase i")) return "text-oxred";
  if (s.includes("ind")) return "text-ink";
  if (s.includes("preclinical")) return "text-ink/70";
  return "text-ink/50";
};

export default function AdminLibrary() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const idx = await libraryIndex();
        setItems(idx);
        if (idx.length > 0) setSelectedId(idx[0].id);
      } catch {
        toast.error("Could not load library.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        setDetail(await libraryDetail(selectedId));
      } catch {
        toast.error("Could not load project detail.");
      }
    })();
  }, [selectedId]);

  if (loading) return <div className="py-24 text-center font-mono-tight text-[11px] text-ink/60">Loading…</div>;
  if (items.length === 0) return <div className="py-24 text-center font-mono-tight text-[11px] text-ink/60">Library empty.</div>;

  return (
    <div data-testid="admin-library" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
      {/* Sidebar */}
      <aside className="lg:col-span-3">
        <div className="font-mono-tight text-[10px] text-ink/60 mb-4 flex items-center gap-2">
          <BookOpen size={12} /> Programs · {items.length}
        </div>
        <div className="border border-ink/15 divide-y divide-ink/10">
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              data-testid={`library-tab-${p.id}`}
              className={`block w-full text-left px-5 py-4 transition-colors ${
                selectedId === p.id ? "bg-ink text-bone" : "bg-bone text-ink hover:bg-bone-2"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-sm">{p.id}</span>
                <span className={`font-mono-tight text-[9px] ${selectedId === p.id ? "text-bone/70" : "text-ink/50"}`}>
                  {p.priority}
                </span>
              </div>
              <div className={`font-display text-lg leading-tight mt-1 ${selectedId === p.id ? "text-bone" : "text-ink"}`}>
                {p.codename}
              </div>
              <div className={`text-xs mt-1 ${selectedId === p.id ? "text-bone/70" : "text-ink/60"}`}>
                {p.compound_class}
              </div>
              <div className={`font-mono-tight text-[9px] mt-2 ${selectedId === p.id ? "text-bone/60" : stageColor(p.stage)}`}>
                {p.stage}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 border border-ink/15 p-5">
          <div className="font-mono-tight text-[10px] text-ink/60 mb-3 flex items-center gap-2">
            <FileText size={12} /> Outgoing NDAs
          </div>
          <button
            data-testid="download-nda-scimplify"
            onClick={async () => {
              try {
                const t = localStorage.getItem(TOKEN_KEY);
                const res = await fetch(`${BACKEND_URL}/api/admin/nda/outgoing/scimplify.pdf`, {
                  headers: { Authorization: `Bearer ${t}` },
                });
                if (!res.ok) throw new Error("Failed");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "NGP-NDA-Scimplify-LThreoMPH.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Downloaded.");
              } catch {
                toast.error("Download failed.");
              }
            }}
            className="w-full inline-flex items-center justify-between gap-2 px-4 py-3 bg-ink text-bone font-mono-tight text-[10px] hover:bg-ink-2 transition-colors"
          >
            <span>Scimplify · L-Threo-MPH</span>
            <FileDown size={12} />
          </button>
          <div className="mt-3 font-mono-tight text-[9px] text-ink/50 leading-relaxed">
            Draft bilateral NDA covering procurement, shipping and Swiss BetmG/BetmV regulatory handling of L-Threo-methylphenidate (CAS 20748-11-2).
          </div>
        </div>
      </aside>

      {/* Detail */}
      <div className="lg:col-span-9">
        {detail ? (
          <article data-testid={`library-detail-${detail.id}`}>
            <div className="flex items-baseline gap-4 flex-wrap mb-2">
              <span className="font-mono-tight text-[10px] text-oxred">{detail.id}</span>
              <span className="font-mono-tight text-[10px] text-ink/50">·</span>
              <span className="font-mono-tight text-[10px] text-ink/60">{detail.priority}</span>
              <span className="ml-auto font-mono-tight text-[10px] text-ink/50 flex items-center gap-1">
                <Clock size={10} /> Updated {detail.last_updated}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tight leading-[0.98]">
              {detail.codename}
            </h2>
            <div className="mt-2 font-display text-xl italic text-oxred">{detail.compound_class}</div>
            <p className="mt-4 text-ink/70 text-sm">
              {detail.indication} · <span className="font-mono-tight text-[10px] text-ink/60">{detail.stage}</span>
              {detail.responsible_partner && (
                <> · <span className="text-ink/60">{detail.responsible_partner}</span></>
              )}
            </p>

            <div className="mt-10 space-y-2">
              <Section title="Target product profile" icon={FileText} testid="section-tpp">
                <KV items={Object.entries(detail.target_product_profile || {})} />
              </Section>

              <Section title="Mechanism (working detail)" icon={Beaker} testid="section-mech">
                <p className="text-ink/90 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                  {detail.mechanism_detail}
                </p>
              </Section>

              <Section title="Assay cascade" icon={Beaker} testid="section-assays">
                <div className="grid grid-cols-12 font-mono-tight text-[9px] text-ink/60 py-2 border-y border-ink/15 uppercase">
                  <div className="col-span-1">Tier</div>
                  <div className="col-span-5">Assay</div>
                  <div className="col-span-6">Acceptance</div>
                </div>
                {(detail.assay_cascade || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 py-3 border-b border-ink/10 text-sm">
                    <div className="col-span-1 font-mono text-xs">{a.tier}</div>
                    <div className="col-span-5">{a.assay}</div>
                    <div className="col-span-6 text-ink/80">{a.acceptance}</div>
                  </div>
                ))}
              </Section>

              <Section title="Chemistry" icon={Beaker} testid="section-chem">
                <KV items={[
                  ["Route", detail.chemistry?.route_type],
                  ["Key starting materials", (detail.chemistry?.key_starting_materials || []).join(" · ")],
                  ["Impurity strategy", detail.chemistry?.impurity_control_strategy],
                ].filter(([, v]) => v)} />
                <div className="mt-4">
                  <div className="font-mono-tight text-[10px] text-ink/60 uppercase mb-2">Critical steps</div>
                  <div className="divide-y divide-ink/10 border-y border-ink/15">
                    {(detail.chemistry?.critical_steps || []).map((s, i) => (
                      <div key={i} className="grid grid-cols-12 gap-3 py-3 text-sm">
                        <div className="col-span-4 font-mono text-xs">{s.step}</div>
                        <div className="col-span-8">{s.control}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {detail.chemistry?.ipc && (
                  <div className="mt-4">
                    <div className="font-mono-tight text-[10px] text-ink/60 uppercase mb-2">In-process controls</div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-ink/80">
                      {detail.chemistry.ipc.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                )}
              </Section>

              <Section title="Analytics" icon={Beaker} testid="section-analytics">
                <div className="grid grid-cols-12 font-mono-tight text-[9px] text-ink/60 py-2 border-y border-ink/15 uppercase">
                  <div className="col-span-4">Method</div>
                  <div className="col-span-4">Purpose</div>
                  <div className="col-span-4">Spec</div>
                </div>
                {(detail.analytics || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 py-3 border-b border-ink/10 text-sm">
                    <div className="col-span-4">{a.method}</div>
                    <div className="col-span-4 text-ink/80">{a.purpose}</div>
                    <div className="col-span-4 font-mono text-xs">{a.spec}</div>
                  </div>
                ))}
              </Section>

              <Section title="Regulatory deliverables" icon={FileText} testid="section-reg">
                <ul className="list-disc list-inside space-y-1 text-sm text-ink/85">
                  {(detail.regulatory_deliverables || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </Section>

              <Section title="Roles &amp; deliverables" icon={FileText} testid="section-roles">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
                  {Object.entries(detail.roles_and_deliverables || {}).map(([role, items2]) => (
                    <div key={role} className="bg-bone p-5">
                      <div className="font-display text-lg tracking-tight">{role}</div>
                      <ul className="list-disc list-inside space-y-1 mt-3 text-sm text-ink/85">
                        {items2.map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Milestones" icon={Milestone} testid="section-milestones">
                <div className="grid grid-cols-12 font-mono-tight text-[9px] text-ink/60 py-2 border-y border-ink/15 uppercase">
                  <div className="col-span-1">Code</div>
                  <div className="col-span-6">Title</div>
                  <div className="col-span-3">Due</div>
                  <div className="col-span-2">Status</div>
                </div>
                {(detail.milestones || []).map((m) => {
                  const statusColor = m.status === "Achieved"
                    ? "text-oxred"
                    : m.status === "On track" ? "text-ink" : "text-ink/50";
                  return (
                    <div key={m.code} className="grid grid-cols-12 gap-2 py-3 border-b border-ink/10 text-sm items-center">
                      <div className="col-span-1 font-mono text-xs">{m.code}</div>
                      <div className="col-span-6">{m.title}</div>
                      <div className="col-span-3 font-mono-tight text-[10px] text-ink/60">{m.due}</div>
                      <div className={`col-span-2 font-mono-tight text-[10px] ${statusColor}`}>{m.status}</div>
                    </div>
                  );
                })}
              </Section>

              <Section title="Risks &amp; mitigations" icon={ShieldAlert} testid="section-risks">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15">
                  {(detail.risks_and_mitigations || []).map((r, i) => (
                    <div key={i} className="bg-bone p-5">
                      <div className="font-mono-tight text-[10px] text-oxred">RISK</div>
                      <div className="text-sm mt-2 text-ink">{r.risk}</div>
                      <div className="font-mono-tight text-[10px] text-ink/60 mt-4">MITIGATION</div>
                      <div className="text-sm mt-2 text-ink/80">{r.mitigation}</div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="IP status" icon={FileText} testid="section-ip">
                <p className="text-sm text-ink/85">{detail.ip_status}</p>
              </Section>

              <Section title="References" icon={BookOpen} testid="section-refs">
                <ul className="space-y-2 text-xs text-ink/75 leading-relaxed">
                  {(detail.references || []).map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </Section>
            </div>
          </article>
        ) : (
          <div className="py-24 text-center font-mono-tight text-[11px] text-ink/60">Select a program.</div>
        )}
      </div>
    </div>
  );
}
